/**
 * OutreachConfigModal — admin-only form to configure the email provider
 * (SMTP, SendGrid or Mailgun) that powers the unified outreach engine
 * (trial nurture + lead sequences). Saved into
 * db.integration_secrets[key='email_provider'].
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Mail, ShieldCheck, Loader2, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OutreachConfigModal({ open, onClose, onSaved }) {
  const { lang } = useLang();
  const isPt = lang === "pt";

  const [provider, setProvider] = useState("smtp");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("AgenteLead Pro");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpSecure, setSmtpSecure] = useState("tls");
  const [apiKey, setApiKey] = useState("");
  const [mailgunDomain, setMailgunDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ configured: false });
  // Iter47 — Auto-outreach kill-switch (persisted server-side).
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [autoBusy, setAutoBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Iter47 — load the auto-outreach toggle state.
    axios.get(`${API}/outreach/settings/auto-outreach`)
      .then(({ data }) => setAutoEnabled(data.enabled !== false))
      .catch(() => {});
    axios.get(`${API}/outreach/config/status`).then(({ data }) => {
      setStatus(data);
      if (data.from_email) setFromEmail(data.from_email);
      if (data.from_name) setFromName(data.from_name);
      if (data.provider) setProvider(data.provider);
    }).catch(() => {});
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { provider, from_email: fromEmail, from_name: fromName };
      if (provider === "smtp") {
        payload.smtp_host = smtpHost;
        payload.smtp_port = Number(smtpPort);
        payload.smtp_username = smtpUser;
        payload.smtp_password = smtpPass;
        payload.smtp_secure = smtpSecure;
      } else if (provider === "sendgrid") {
        payload.api_key = apiKey;
      } else if (provider === "mailgun") {
        payload.api_key = apiKey;
        payload.mailgun_domain = mailgunDomain;
      } else if (provider === "resend") {
        // Resend (https://resend.com) — only requires an API key. The same
        // backend `api_key` field carries it; the from_email must be on a
        // domain you've verified inside the Resend dashboard.
        payload.api_key = apiKey;
      }
      const { data } = await axios.post(`${API}/outreach/config`, payload);
      toast.success(isPt ? `Provedor ${data.provider} salvo` : `${data.provider} provider saved`);
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="outreach-config-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[18px] font-bold">
            <Mail className="w-5 h-5 text-[#3B82F6]" />
            {isPt ? "Configurar provedor de email" : "Configure email provider"}
            {status.configured && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">
                {status.provider} ok
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#6B7280]">
            {isPt
              ? "Esta configuração alimenta o motor unificado: nutrição do trial + sequências de cold-email nos leads. Os dados ficam apenas na sua DB."
              : "This config powers the unified outreach engine: trial nurture + lead cold-email sequences. Stored only in your DB."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-3">
          {/* Iter47 — Auto-outreach kill-switch (persists server-side). */}
          <div
            data-testid="auto-outreach-toggle-row"
            className={`rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3 ${
              autoEnabled ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"
            }`}
          >
            <div className="min-w-0">
              <div className="text-[12.5px] font-bold text-[#111827]">
                {isPt ? "Disparo automático de cold-email" : "Automatic cold-email trigger"}
              </div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">
                {isPt
                  ? "Quando um lead NEW/CONTACTED é detectado com ads ativos no Meta, um e-mail GPT é enviado automaticamente. Desligue para tomar controle manual via o botão 📧 no card."
                  : "When a NEW/CONTACTED lead is detected running live Meta ads, a GPT email is dispatched automatically. Disable for manual control via the 📧 card button."}
              </div>
            </div>
            <button
              type="button"
              disabled={autoBusy}
              data-testid="auto-outreach-toggle"
              onClick={async () => {
                setAutoBusy(true);
                const next = !autoEnabled;
                try {
                  await axios.put(`${API}/outreach/settings/auto-outreach`, { enabled: next });
                  setAutoEnabled(next);
                  toast.success(
                    next
                      ? (isPt ? "Disparo automático ATIVADO" : "Auto-dispatch ENABLED")
                      : (isPt ? "Disparo automático DESATIVADO" : "Auto-dispatch DISABLED"),
                  );
                } catch (_e) {
                  toast.error(isPt ? "Falha ao salvar" : "Save failed");
                } finally { setAutoBusy(false); }
              }}
              className={`shrink-0 h-8 px-4 rounded-full text-[11.5px] font-extrabold uppercase tracking-wider transition-colors ${
                autoEnabled
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-amber-500 text-white hover:bg-amber-600"
              } disabled:opacity-60`}
            >
              {autoBusy ? "…" : autoEnabled ? (isPt ? "Ativo" : "On") : (isPt ? "Desativo" : "Off")}
            </button>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-[#6B7280] font-semibold mb-1.5">
              {isPt ? "Provedor" : "Provider"}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {["smtp", "sendgrid", "mailgun", "resend"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`outreach-provider-${p}`}
                  onClick={() => setProvider(p)}
                  className={
                    "h-9 rounded-lg text-[12px] font-bold uppercase transition-colors " +
                    (provider === p
                      ? "bg-[#3B82F6] text-white"
                      : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]")
                  }
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label>
              <div className="text-[10.5px] uppercase tracking-wide text-[#6B7280] font-semibold mb-1.5">
                {isPt ? "From email" : "From email"}
              </div>
              <input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}
                placeholder="sender@yourdomain.com" data-testid="outreach-from-email"
                className="w-full al-input h-9 text-[12.5px]" />
            </label>
            <label>
              <div className="text-[10.5px] uppercase tracking-wide text-[#6B7280] font-semibold mb-1.5">
                {isPt ? "From nome" : "From name"}
              </div>
              <input type="text" value={fromName} onChange={(e) => setFromName(e.target.value)}
                data-testid="outreach-from-name"
                className="w-full al-input h-9 text-[12.5px]" />
            </label>
          </div>

          {provider === "smtp" && (
            <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-[#E5E7EB]">
              <label className="sm:col-span-2">
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">SMTP host</div>
                <input type="text" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.gmail.com / smtp.office365.com" data-testid="outreach-smtp-host"
                  className="w-full al-input h-9 text-[12.5px]" />
              </label>
              <label>
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">SMTP port</div>
                <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)}
                  data-testid="outreach-smtp-port"
                  className="w-full al-input h-9 text-[12.5px] tabular-nums" />
              </label>
              <label>
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">{isPt ? "Segurança" : "Security"}</div>
                <select value={smtpSecure} onChange={(e) => setSmtpSecure(e.target.value)}
                  data-testid="outreach-smtp-secure"
                  className="w-full al-input h-9 text-[12.5px]">
                  <option value="tls">STARTTLS (587)</option>
                  <option value="ssl">SSL (465)</option>
                  <option value="none">Plain (25)</option>
                </select>
              </label>
              <label>
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">{isPt ? "Usuário" : "Username"}</div>
                <input type="text" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)}
                  data-testid="outreach-smtp-user"
                  className="w-full al-input h-9 text-[12.5px]" />
              </label>
              <label>
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">{isPt ? "Senha / App password" : "Password / App password"}</div>
                <input type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)}
                  data-testid="outreach-smtp-pass"
                  className="w-full al-input h-9 text-[12.5px]" />
              </label>
            </div>
          )}

          {(provider === "sendgrid" || provider === "mailgun" || provider === "resend") && (
            <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-[#E5E7EB]">
              <label className="sm:col-span-2">
                <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">API key</div>
                <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    provider === "sendgrid" ? "SG.xxxxxxxx" :
                    provider === "mailgun" ? "key-xxxxxxxx" :
                    "re_xxxxxxxx (from resend.com/api-keys)"
                  }
                  data-testid="outreach-api-key"
                  className="w-full al-input h-9 text-[12.5px]" />
                {provider === "resend" && (
                  <div className="text-[10.5px] text-[#6B7280] mt-1.5 leading-snug">
                    {isPt
                      ? "Crie uma key em resend.com/api-keys. Lembre de verificar seu domínio antes de enviar (Resend → Domains)."
                      : "Create a key at resend.com/api-keys. Make sure to verify your sender domain (Resend → Domains) before sending."}
                  </div>
                )}
              </label>
              {provider === "mailgun" && (
                <label className="sm:col-span-2">
                  <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">Mailgun domain</div>
                  <input type="text" value={mailgunDomain} onChange={(e) => setMailgunDomain(e.target.value)}
                    placeholder="mg.yourdomain.com" data-testid="outreach-mailgun-domain"
                    className="w-full al-input h-9 text-[12.5px]" />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
          <button type="button" onClick={onClose}
            className="px-4 h-9 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold hover:bg-[#FAFAFB] inline-flex items-center gap-1.5">
            <X className="w-3.5 h-3.5" /> {isPt ? "Cancelar" : "Cancel"}
          </button>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="outreach-config-save"
            className="px-5 h-9 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            {isPt ? "Salvar provedor" : "Save provider"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
